// NOTE: The `global = window` polyfill lives in index.html as an inline <script>
// so it executes before ESM imports are evaluated.  Libraries like draft-js / fbjs
// reference `global` at module-evaluation time, and ESM hoisting means any polyfill
// placed here would run too late.

import * as React from 'react';
import * as ReactDOM from 'react-dom';
// Polyfills the Popover API (`popover` attribute, showPopover()/hidePopover())
// for browsers without native support. No-ops entirely where native support
// already exists. Must be imported before any component calls showPopover().
import '@oddbird/popover-polyfill';
import App from './app/App';
import './shared-design-system/tokens/tokens.css';
import './shared-design-system/styles/popover-reset.css';
import './ant.css';
import './ant-overrides.css';
import ConfigService from './shared/services/ConfigService';
import {ErrorHandler} from './app/context/ErrorHandler';
import DataService, {FetchOperation} from './shared/services/DataService';
import StackTrace from 'stacktrace-js';
import {isNil} from 'lodash-es';

function isBrowserSupported(): boolean {
  // Denylist IE 10, 9 and 8 based on user agent
  const IE_USER_AGENT_REGEX = /MSIE\s?(\d+)(?:\.(\d+))?/i;

  const matches = navigator.userAgent.match(IE_USER_AGENT_REGEX);
  if (matches != null) {
    if (matches[1] === '10' || matches[1] === '9' || matches[1] === '8') {
      return false;
    }
  }

  return true;
}

function reportError(error: Error) {
  const language = navigator.language;
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  StackTrace.fromError(error).then((frames) => {
    const stack = frames.map(f => {
      return f.toString();
    }).join('\n');

    DataService.fetch(
      '/telemetry/report-error',
      {
        errorMessage: error.message,
        stackTrace: stack,
        language: language,
        timeZoneName: timezone
      },
      FetchOperation.Post,
      null,
      null,
      false
    ).then().catch(() => {
        return;
    });
  });
}

function hookErrorHandlers() {
  window.addEventListener(
    'error',
    (e) => {
      if (isNil(e) || isNil(e.error)) {
        return false;
      }

      reportError(e.error);
      return false;
    }
  );
  window.addEventListener(
    'unhandledrejection',
    (e) => {
      if (isNil(e) || isNil(e.reason)) {
        return false;
      }

      if (e.reason instanceof Error) {
        reportError(e.reason);
      } else {
        reportError(new Error(JSON.stringify(e.reason)));
      }
      return false;
    }
  );
}

if (isBrowserSupported()) {
  hookErrorHandlers();
  ConfigService.loadConfig().then(() => {
    ReactDOM.render(
      <ErrorHandler.Provider
        value={{
          reportError: reportError
        }}
      >
        <App/>
      </ErrorHandler.Provider>,
      document.getElementById('root') as HTMLElement
    );
  });
} else {
  (document.getElementById('root') as HTMLElement).innerText = 'Sorry, your browser is not supported';
}
